import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { z } from "zod";
import { ClientLocationsEditor } from "@/components/location/ClientLocationsEditor";
import { useClientLocations } from "@/hooks/use-client-locations";

interface EditClientDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  client: {
    id: string;
    name: string;
    email: string | null;
    phone: string | null;
    notes: string | null;
    previous_session_number?: number;
    current_package_size?: number;
  } | null;
}

const clientSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100, "Name must be less than 100 characters"),
  email: z.string().trim().email("Invalid email address").max(255, "Email must be less than 255 characters").optional().or(z.literal("")),
  phone: z.string().trim().max(20, "Phone must be less than 20 characters").optional().or(z.literal("")),
  notes: z.string().max(1000, "Notes must be less than 1000 characters").optional().or(z.literal("")),
});

const EditClientDialog = ({ open, onOpenChange, onSuccess, client }: EditClientDialogProps) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [sessionsCompleted, setSessionsCompleted] = useState("");
  const [activePackageSize, setActivePackageSize] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { locations, addLocation, updateLocation, deleteLocation, setPrimary } =
    useClientLocations(client?.id);

  useEffect(() => {
    if (open && client) {
      setName(client.name);
      setEmail(client.email || "");
      setPhone(client.phone || "");
      setNotes(client.notes || "");
      setSessionsCompleted(
        client.previous_session_number != null
          ? String(client.previous_session_number)
          : ""
      );

      // Fetch active package size for accurate validation/display
      supabase
        .from("packages")
        .select("size")
        .eq("client_id", client.id)
        .eq("status", "active")
        .order("activated_at", { ascending: false })
        .limit(1)
        .single()
        .then(({ data }) => {
          setActivePackageSize(data?.size ?? null);
        });
    } else {
      setActivePackageSize(null);
    }
  }, [open, client]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!client) return;

    try {
      clientSchema.parse({
        name,
        email: email || "",
        phone: phone || "",
        notes: notes || "",
      });

      const newSessionsCompleted = Math.max(0, parseInt(sessionsCompleted) || 0);
      const pkgSize = activePackageSize ?? client.current_package_size ?? 0;
      if (pkgSize > 0 && (newSessionsCompleted < 0 || newSessionsCompleted >= pkgSize)) {
        toast.error("Sessions completed must be between 0 and the package size");
        return;
      }

      setIsSubmitting(true);

      const sessionsChanged =
        client.previous_session_number != null &&
        newSessionsCompleted !== client.previous_session_number;

      const { error } = await supabase
        .from("clients")
        .update({
          name: name.trim(),
          email: email.trim() || null,
          phone: phone.trim() || null,
          notes: notes.trim() || null,
          ...(sessionsChanged && {
            previous_session_number: newSessionsCompleted,
            upcoming_session_number: newSessionsCompleted + 1,
          }),
        })
        .eq("id", client.id);

      if (error) throw error;

      // Sync active package sessions_completed if changed
      if (sessionsChanged) {
        await supabase
          .from("packages")
          .update({ sessions_completed: newSessionsCompleted })
          .eq("client_id", client.id)
          .eq("status", "active");
      }

      toast.success("Client updated");
      onOpenChange(false);
      onSuccess();
    } catch (error: any) {
      if (error instanceof z.ZodError) {
        toast.error(error.errors[0].message);
      } else {
        console.error("Error updating client:", error);
        toast.error("Failed to update client");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open || !client) return null;

  const effectivePackageSize = activePackageSize ?? client.current_package_size ?? 0;
  const showSessionsField =
    client.previous_session_number != null && effectivePackageSize > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit client</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={handleSubmit}
          onKeyDown={(e) => {
            // Cmd/Ctrl+Enter submits the form from anywhere inside.
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
              e.preventDefault();
              if (!isSubmitting) handleSubmit(e as unknown as React.FormEvent);
            }
          }}
          className="space-y-4"
        >
          <div className="space-y-2">
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              type="text"
              placeholder="Client name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="client@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Phone</Label>
            <Input
              id="phone"
              type="tel"
              placeholder="(555) 123-4567"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              placeholder="Add any notes about this client..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>

          {showSessionsField && (
            <div className="space-y-2">
              <Label htmlFor="sessions-completed">Adjust sessions completed</Label>
              <Input
                id="sessions-completed"
                type="number"
                min="0"
                max={effectivePackageSize - 1}
                value={sessionsCompleted}
                onChange={(e) => setSessionsCompleted(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Remaining: {Math.max(0, effectivePackageSize - (parseInt(sessionsCompleted) || 0))} sessions
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label>Locations</Label>
            <ClientLocationsEditor
              locations={locations}
              onAdd={addLocation}
              onUpdate={updateLocation}
              onDelete={deleteLocation}
              onSetPrimary={setPrimary}
              compact
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Updating..." : "Update Client"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditClientDialog;
